import type { BaseBlog } from '@bulknutrients/bulk-shared-lib/interfaces/Blog';
import { normaliseRoute } from '@bulknutrients/bulk-shared-lib/utils';
import { Button } from '@components/Button';
import { CustomImage } from '@components/Media/Image/Image';
import { SkeletonImage, SkeletonText } from '@components/Placeholders/Skeleton';
import { twMerge } from 'tailwind-merge';

type Props = {
  blog: BaseBlog;
} & Record<string, any>;

export function BlogCard({ blog, ...props }: Props) {
  const slug = normaliseRoute(blog.slug);

  const umamiProps = Object.fromEntries(
    Object.entries(props).filter(([key]) => key.startsWith('data-umami'))
  );
  const extraProps = Object.fromEntries(
    Object.entries(props).filter(
      ([key]) => !key.startsWith('data-umami') && key !== 'className'
    )
  );

  return (
    <div
      className={twMerge([
        props.className,
        'h-full w-[275px] max-w-[275px] rounded-2xl flex flex-col transition-all duration-300 hover:scale-103',
      ])}
      data-umami-event="hue-link-click"
      data-umami-event-slug={slug}
      {...umamiProps}
    >
      <div
        {...extraProps}
        className="m-0 flex h-full flex-col border-none px-0 rounded-t-2xl overflow-hidden"
      >
        <CustomImage
          image={blog.featuredImage}
          width={275}
          height={230}
          className="rounded-none"
        />
        <div className="flex-1 bg-black dark:bg-grey-20">
          <h2 className="my-2 mx-4 text-2xl text-white font-secondary uppercase line-clamp-2 text-ellipsis pointer-events-none">
            {blog.title}
          </h2>
        </div>
      </div>
      <Button
        href={slug}
        buttonStyle="primary"
        className="justify-self-end rounded-t-none rounded-b-2xl hover:bg-grey hover:text-white hover:dark:bg-black hover:dark:text-white"
      >
        Read more
      </Button>
    </div>
  );
}

export function LoadingBlogCard() {
  return (
    <div className="h-full w-[275px] max-w-[275px] rounded-2xl flex flex-col">
      <div className="m-0 flex h-full flex-col border-none px-0 rounded-t-2xl overflow-hidden">
        <SkeletonImage aspectRatio="aspect-[275/230]" widthPixels={275} />
        <div className="flex-1 bg-black dark:bg-grey-20">
          <SkeletonText
            heightPixels={44}
            textHeightPixels={24}
            textClasses="mx-2"
          />
        </div>
      </div>
      <div className="flex-1 bg-orange rounded-b-2xl">
        <SkeletonText
          heightPixels={40}
          textHeightPixels={16}
          textClasses="bg-white mx-8"
        />
      </div>
    </div>
  );
}
